import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Logo from "@/components/Logo";
import { HUB, LABELS } from "@/data/seoPages";
import { useAuth } from "@/context/AuthContext";
import { isEmployer } from "@/lib/roles";

// Public marketing/SEO layout with head tags, header and internal-link footer.
export default function WorkLayout({ title, description, canonicalPath, image, children, jsonLd }) {
  const { user } = useAuth();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = origin + (canonicalPath || "");
  return (
    <div className="min-h-screen wo-bg text-slate-100">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        {image && <meta property="og:image" content={image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {image && <meta name="twitter:image" content={image} />}
        {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      </Helmet>

      <header className="sticky top-0 z-30 backdrop-blur-xl bg-slate-950/70 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo />
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/work/jobs" className="px-3 py-2 rounded-lg text-slate-300 hover:text-teal-300 hover:bg-white/5">All jobs</Link>
            <Link to="/work/live-in-netherlands" className="hidden sm:inline px-3 py-2 rounded-lg text-slate-300 hover:text-teal-300 hover:bg-white/5">Live &amp; work in NL</Link>
            {user ? (
              <Link to={isEmployer(user.role) ? "/dashboard" : "/jobs"} data-testid="work-app-cta" className="px-4 py-2 rounded-lg bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold">Go to my account</Link>
            ) : (
              <>
                <Link to="/login" className="px-3 py-2 rounded-lg text-slate-300 hover:text-teal-300 hover:bg-white/5">Log in</Link>
                <Link to="/register" data-testid="work-register-cta" className="px-4 py-2 rounded-lg bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold">Create profile</Link>
              </>
            )}
          </nav>
        </div>
      </header>

      <main>{children}</main>

      <footer className="border-t border-white/10 mt-16 bg-slate-950/60">
        <div className="max-w-6xl mx-auto px-5 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="text-slate-200 font-semibold mb-3">Top cities</div>
            <ul className="space-y-2">
              {HUB.cities.map((s) => (
                <li key={s}><Link to={`/work/jobs/${s}`} className="text-slate-400 hover:text-teal-300">{LABELS[s]}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-slate-200 font-semibold mb-3">Sectors</div>
            <ul className="space-y-2">
              {HUB.categories.map((s) => (
                <li key={s}><Link to={`/work/jobs/${s}`} className="text-slate-400 hover:text-teal-300">{LABELS[s]} jobs</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-slate-200 font-semibold mb-3">Guides</div>
            <ul className="space-y-2">
              <li><Link to="/work/live-in-netherlands" className="text-slate-400 hover:text-teal-300">Live &amp; work in the Netherlands</Link></li>
              <li><Link to="/work/jobs/netherlands" className="text-slate-400 hover:text-teal-300">Jobs in the Netherlands</Link></li>
              <li><Link to="/work/jobs" className="text-slate-400 hover:text-teal-300">Browse all jobs</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-slate-200 font-semibold mb-3">WorkOnIT</div>
            <ul className="space-y-2">
              <li><Link to="/register" className="text-slate-400 hover:text-teal-300">Create a candidate profile</Link></li>
              <li><Link to="/register" className="text-slate-400 hover:text-teal-300">For employers &amp; agencies</Link></li>
              <li><Link to="/legal" className="text-slate-400 hover:text-teal-300">Legal &amp; privacy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 py-5 text-center text-xs text-slate-500 px-5">
          WorkOnIT is a directory and platform, not an employment agency. Owned by JamesBass Technologies (a division of JamesBass Holding LLC).
        </div>
      </footer>
    </div>
  );
}
